import { defineMessages, type IntlShape } from 'react-intl'
import LineCameraPlusIcon from 'LuxKit/Icons/line/LineCameraPlusIcon'
import LineCoffeeIcon from 'LuxKit/Icons/line/LineCoffeeIcon'
import LineDrinksTrayIcon from 'LuxKit/Icons/line/LineDrinksTrayIcon'
import LineFootballBallIcon from 'LuxKit/Icons/line/LineFootballBallIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineShoppingBagIcon from 'LuxKit/Icons/line/LineShoppingBagIcon'
import LineTreesIcon from 'LuxKit/Icons/line/LineTreesIcon'
import LineUniversityIcon from 'LuxKit/Icons/line/LineUniversityIcon'

const messages = defineMessages({
  topAttractions: {
    id: 'BArEXtaG4C-7mC44bBbR',
    defaultMessage: 'Top attractions',
    description: 'Explore category label for top attractions in trip planner',
  },
  restaurants: {
    id: 'Zhmx4evw7tHW5XcGtvrF',
    defaultMessage: 'Restaurants',
    description: 'Explore category label for restaurants in trip planner',
  },
  cafes: {
    id: 'NDvzKeBwp8OrP2YAA_5h',
    defaultMessage: 'Cafes',
    description: 'Explore category label for cafes in trip planner in the trip planner explore section',
  },
  parks: {
    id: 'IYg4UAgvsNZAsd_fvuG0',
    defaultMessage: 'Parks',
    description: 'Explore category label for parks in trip planner in the trip planner explore section',
  },
  shopping: {
    id: 'Zh0cTYm14QwEqK1evsp6',
    defaultMessage: 'Shopping',
    description: 'Explore category label for shopping in trip planner',
  },
  museums: {
    id: '9ORQZfVZJS2iSJ3Utn-p',
    defaultMessage: 'Museums',
    description: 'Explore category label for museums in trip planner',
  },
  bars: {
    id: '7TBgAo45mI_BHa2uBXJS',
    defaultMessage: 'Bars',
    description: 'Explore category label for bars in trip planner in the trip planner explore section',
  },
  sports: {
    id: '8Mpq7w5KK4g1yl6YntXZ',
    defaultMessage: 'Sports',
    description: 'Explore category label for sports in trip planner in the trip planner explore section',
  },
  topAttractionsQuery: {
    id: 'ka_ee2R3_ZHs6oSi7Zvi',
    defaultMessage: 'Top attractions in {placeName}',
    description: 'Search query sent for top attractions in a selected place in the trip planner explore section',
  },
  restaurantsQuery: {
    id: 'HwuL2FYt82vBAi7K8eKM',
    defaultMessage: 'Restaurants in {placeName}',
    description: 'Search query sent for restaurants in a selected place in the trip planner explore section',
  },
  cafesQuery: {
    id: 'bvX8eFx6ImqLwQIdCYtj',
    defaultMessage: 'Cafes in {placeName}',
    description: 'Search query sent for cafes in a selected place in the trip planner explore section',
  },
  parksQuery: {
    id: 'j-e9zne3lrqtWwOCMrNm',
    defaultMessage: 'Parks in {placeName}',
    description: 'Search query sent for parks in a selected place in the trip planner explore section',
  },
  shoppingQuery: {
    id: 'fK5ZJZgZ2v739grMxFH8',
    defaultMessage: 'Shopping in {placeName}',
    description: 'Search query sent for shopping in a selected place in the trip planner explore section',
  },
  museumsQuery: {
    id: '38sDl7MJhkDL_xNLWaam',
    defaultMessage: 'Museums in {placeName}',
    description: 'Search query sent for museums in a selected place in the trip planner explore section',
  },
  barsQuery: {
    id: '-EAZSqIz9ebxGqez1Jh9',
    defaultMessage: 'Bars in {placeName}',
    description: 'Search query sent for bars in a selected place in the trip planner explore section',
  },
  sportsQuery: {
    id: 'mlkbZlyWcKJ-AsGFCN3w',
    defaultMessage: 'Sports venues in {placeName}',
    description: 'Search query sent for sports venues in a selected place in the trip planner explore section',
  },
})

export type ExploreCategoryKey =
  | 'top-attractions'
  | 'restaurants'
  | 'cafes'
  | 'parks'
  | 'shopping'
  | 'museums'
  | 'bars'
  | 'sports'

export const exploreCategories = [
  {
    key: 'top-attractions' as const,
    label: 'Top attractions',
    labelMessage: messages.topAttractions,
    icon: LineCameraPlusIcon,
  },
  {
    key: 'restaurants' as const,
    label: 'Restaurants',
    labelMessage: messages.restaurants,
    icon: LineRestaurantIcon,
  },
  {
    key: 'cafes' as const,
    label: 'Cafes',
    labelMessage: messages.cafes,
    icon: LineCoffeeIcon,
  },
  {
    key: 'parks' as const,
    label: 'Parks',
    labelMessage: messages.parks,
    icon: LineTreesIcon,
  },
  {
    key: 'shopping' as const,
    label: 'Shopping',
    labelMessage: messages.shopping,
    icon: LineShoppingBagIcon,
  },
  {
    key: 'museums' as const,
    label: 'Museums',
    labelMessage: messages.museums,
    icon: LineUniversityIcon,
  },
  {
    key: 'bars' as const,
    label: 'Bars',
    labelMessage: messages.bars,
    icon: LineDrinksTrayIcon,
  },
  {
    key: 'sports' as const,
    label: 'Sports',
    labelMessage: messages.sports,
    icon: LineFootballBallIcon,
  },
] as const

export type ExploreCategory = (typeof exploreCategories)[number]

type ExploreCategoryAnalyticsLabels = {
  [K in (typeof exploreCategories)[number]['key']]: string
}

export const exploreAnalyticsCategoryLabels: ExploreCategoryAnalyticsLabels = {
  'top-attractions': 'top-attractions',
  restaurants: 'restaurants',
  cafes: 'cafes',
  parks: 'parks',
  shopping: 'shopping',
  museums: 'museums',
  bars: 'bars',
  sports: 'sports',
}

const exploreQueryMessages: Record<ExploreCategory['key'], typeof messages.topAttractionsQuery> = {
  'top-attractions': messages.topAttractionsQuery,
  restaurants: messages.restaurantsQuery,
  cafes: messages.cafesQuery,
  parks: messages.parksQuery,
  shopping: messages.shoppingQuery,
  museums: messages.museumsQuery,
  bars: messages.barsQuery,
  sports: messages.sportsQuery,
}

export function formatExploreQuery(
  intl: IntlShape,
  place: { lePlaceName: string },
  category: ExploreCategory,
) {
  return intl.formatMessage(exploreQueryMessages[category.key], {
    placeName: place.lePlaceName,
  })
}
